import React from "react";
import MuiBox from "@mui/material/Box";
import PropTypes from "prop-types";

/**
 * Box component wrapper for layout and spacing utilities.
 * Wraps Material-UI Box with full system props support.
 * 
 * @component
 * @example
 * <Box mt={2} ml={3} p={2}>
 *   Content
 * </Box>
 * 
 * @example
 * <Box sx={{ display: 'flex', gap: 2 }}>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Box>
 */
const Box = React.forwardRef(({ children, ...props }, ref) => {
  return <MuiBox ref={ref} {...props}>{children}</MuiBox>;
});

Box.displayName = 'Box';

Box.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * Margin top (theme spacing multiplier)
   */
  mt: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin right (theme spacing multiplier)
   */
  mr: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin bottom (theme spacing multiplier)
   */
  mb: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin left (theme spacing multiplier)
   */
  ml: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin x-axis (theme spacing multiplier)
   */
  mx: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin y-axis (theme spacing multiplier)
   */
  my: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Margin (theme spacing multiplier)
   */
  m: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding top (theme spacing multiplier)
   */
  pt: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding right (theme spacing multiplier)
   */
  pr: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding bottom (theme spacing multiplier)
   */
  pb: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding left (theme spacing multiplier)
   */
  pl: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding x-axis (theme spacing multiplier)
   */
  px: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding y-axis (theme spacing multiplier)
   */
  py: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * Padding (theme spacing multiplier)
   */
  p: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * ARIA role for accessibility
   */
  role: PropTypes.string,
};

export default Box;

